import { SortableTh, Table, Tbody, Td, Tfoot, Thead, Tr } from 'components/ui-blocks/table';
import config from 'config';
import { isAgentOnlyWidget, isAgentReportColumn } from 'constants/agentReporting';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { COLUMNS, Column } from 'constants/filters';
import {
  COLUMN_DAY,
  COLUMN_NAME,
  COLUMN_PROJECT,
  COLUMN_STATE,
  PROGRESS_BAR_COLUMNS,
  WIDGET_TYPE_TO_QUERY_FILTER_MAP,
} from 'constants/filters';
import { WIDGET_DATA_SUBJECTS, WIDGET_LABELS_HBAR } from 'constants/widgets';
import { columnNameFactory } from 'factories/superreports';
import { useWidgetResizeObserver } from 'hooks/useWidgetResizeObserver';
import { without } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FetchHoursForParams } from 'redux/modules/superreports.actions';
import type {
  GenericWidgetRow,
  ToggleSelectionParams,
  WidgetMetadataDictionary,
  WidgetReference,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import type { Currency, Features } from 'types/account';
import { TagFilterMode } from 'types/tag';
import type { CostAndDurationSummary } from 'utils/summary';
import { areWidgetRowsExpandable, filterAvailableColumns } from 'utils/superreports';
import { notNull } from 'utils/typesafe';
import { useViewportWidthBreakpoint } from 'utils/viewport';
import {
  NO_DATA_COLUMNS_MESSAGE,
  NO_DATA_FILTERS_MESSAGE,
  NO_DATA_TAG_FILTER_MESSAGE,
} from '../constants';
import NoData from '../NoData';
import AgentSessionRows from './AgentSessionRows';
import type { TableColumnChangeParams } from './helpers';
import { filterVisibleColumns } from './helpers';
import TableWidgetBodyRow from './rows/TableWidgetBodyRow';
import TableWidgetHourDetailsRow from './rows/TableWidgetHourDetailsRow';
import TableWidgetLoadMoreRow from './rows/TableWidgetLoadMoreRow';
import TableWidgetPlaceholderRow from './rows/TableWidgetPlaceholderRow';
import styles from './TableWidget.module.scss';
import TableWidgetLoading from './TableWidgetLoading';

interface Props {
  batchSelectCheckbox?: React.ReactNode;
  billedState: boolean | null;
  tagFilterMode: TagFilterMode;
  currency: Currency;
  isLoading?: boolean;
  isSaving?: boolean;
  hiddenColumns?: Array<Column>;
  hideEntries?: boolean;
  hoursOnly?: boolean;
  noHeader?: boolean;
  stickyHeader?: boolean;
  readOnly?: boolean;
  canSelectReadonlyHours?: boolean;
  rowLines?: boolean;
  totals?: CostAndDurationSummary;
  widget: WidgetWithData;
  entityMetadata: WidgetMetadataDictionary;
  features: Features;
  className?: string;

  onColumnChange?: (a: TableColumnChangeParams) => void;
  onCreateHourDraft: (id: number) => void;
  onFetchReportHoursFor?: (params: FetchHoursForParams) => void;
  onToggleSelection?: (params: ToggleSelectionParams) => void;
}

const TableWidgetBody: React.FC<Props> = ({
  batchSelectCheckbox,
  billedState,
  tagFilterMode,
  currency,
  entityMetadata,
  features,
  hiddenColumns = EMPTY_ARRAY,
  hideEntries,
  isLoading,
  isSaving,
  hoursOnly,
  noHeader,
  stickyHeader,
  readOnly,
  canSelectReadonlyHours,
  rowLines = false,
  widget,
  onColumnChange,
  onCreateHourDraft,
  onFetchReportHoursFor,
  onToggleSelection,
  className,
}) => {
  const reference: WidgetReference = (widget.id ?? widget._ref)!;

  const [tableRef, setTableRef] = useState<HTMLTableElement | null>(null);
  const currentWidthBreakpoint = useViewportWidthBreakpoint();
  useWidgetResizeObserver(tableRef, currentWidthBreakpoint);

  // We can use the adjacent sibling selector in CSS to apply hover styles to the hour details when
  // hovering over the hour. However, there's no "previous adjacent sibling" selector in CSS, so we
  // need to use JavaScript to make it bidirectional.
  const rowHoverRef = useRef<HTMLTableRowElement | null>();
  useEffect(() => {
    if (!tableRef) return;

    const onMouseOver = (event: Event) => {
      const targetRow = (event.target as HTMLElement)?.closest('tr');
      if (!targetRow || (rowHoverRef.current && targetRow === rowHoverRef.current)) return;

      if (targetRow.classList.contains(styles.hourDetailsRow)) {
        rowHoverRef.current = targetRow.previousElementSibling as HTMLTableRowElement;
        rowHoverRef.current.classList.add(styles.hourRowAdjacentHover);
      }
    };

    const onMouseOut = (event: Event) => {
      const targetRow = (event.target as HTMLElement)?.closest('tr');
      if (!targetRow || !rowHoverRef.current) return;

      rowHoverRef.current.classList.remove(styles.hourRowAdjacentHover);
      rowHoverRef.current = null;
    };

    tableRef.addEventListener('mouseover', onMouseOver);
    tableRef.addEventListener('mouseout', onMouseOut);

    return () => {
      tableRef.removeEventListener('mouseover', onMouseOver);
      tableRef.removeEventListener('mouseout', onMouseOut);
    };
  }, [tableRef]);

  const handleColumnChange = (column: Column) => {
    const { sort_by, order } = widget.columns;

    if (column === sort_by) {
      onColumnChange?.({
        order: order === 'asc' ? 'desc' : 'asc',
        reference,
        sort_by: column,
      });
    } else {
      onColumnChange?.({
        order: column === COLUMN_NAME ? 'asc' : 'desc',
        reference,
        sort_by: column,
      });
    }
  };

  const columns = useMemo(
    () =>
      features.agent_tracking && !config.isPublicReport
        ? widget.columns
        : {
            ...widget.columns,
            names: widget.columns.names.filter(column => !isAgentReportColumn(column)),
          },
    [features.agent_tracking, widget.columns]
  );
  const agentOnly = isAgentOnlyWidget(columns.names);
  const agentColumns = columns.names.filter(isAgentReportColumn);

  const availableTableColumns = useMemo(() => {
    const metadataColumns = entityMetadata[widget.widget_type]?.columns;
    return metadataColumns
      ? filterAvailableColumns(
          metadataColumns,
          widget.widget_type,
          billedState,
          features.capacity_reports,
          readOnly
        )
      : columns.names;
  }, [billedState, columns.names, entityMetadata, features, widget.widget_type, readOnly]);
  const tableColumns = useMemo(
    () => filterVisibleColumns(without(columns.names, ...hiddenColumns), availableTableColumns),
    [availableTableColumns, columns.names, hiddenColumns]
  );

  const hourColumns = columns.names as Array<(typeof COLUMNS)[number]>;

  const noHoursPlaceholder = useMemo(() => {
    const dataSubject = WIDGET_DATA_SUBJECTS[widget.widget_type];
    return dataSubject ? `No matching hours for this ${dataSubject}.` : 'No matching hours.';
  }, [widget.widget_type]);

  const handleLoadHours = (rowId: number, shouldExpandRow = true, uri?: string) => {
    onFetchReportHoursFor?.({
      expandRow: shouldExpandRow,
      filterScope: WIDGET_TYPE_TO_QUERY_FILTER_MAP[widget.widget_type],
      id: rowId,
      uri, // For linked_work widget, pass URI for filtering
      widgetReference: reference,
    });
  };

  const renderFooterRow = (row: GenericWidgetRow) => (
    <Tfoot key="footer" className={styles.footer}>
      <TableWidgetBodyRow
        data-testid="table-widget-body"
        data={row}
        columns={hourColumns}
        currentWidthBreakpoint={currentWidthBreakpoint}
        expandedState={null}
      />
    </Tfoot>
  );

  const renderHourRow = (hour: GenericWidgetRow, depth: number) => {
    const canUpdate = canSelectReadonlyHours || (!readOnly && onToggleSelection && hour.canManage);
    const hasBatchSelect = !!batchSelectCheckbox;

    return [
      <TableWidgetBodyRow
        key={hour.id}
        data={hour}
        columns={hourColumns}
        depth={depth}
        selectedState={hour.isSelected}
        onEdit={() => onCreateHourDraft(hour.id!)}
        onToggleSelection={
          canUpdate
            ? () =>
                onToggleSelection?.({
                  selectionKey: 'hourIds',
                  value: hour.id,
                })
            : undefined
        }
        isLoading={isLoading}
        readOnly={!canUpdate}
        expandedState={null}
        isHour
        currency={currency}
        currentWidthBreakpoint={currentWidthBreakpoint}
        hiddenColumns={hiddenColumns}
        hasBatchSelect={hasBatchSelect}
      />,
      <TableWidgetHourDetailsRow
        key={`${hour.id}-details`}
        data={hour}
        columns={hourColumns}
        depth={depth}
        onEdit={() => onCreateHourDraft(hour.id!)}
        readOnly={!canUpdate}
        expandedState={null}
        currentWidthBreakpoint={currentWidthBreakpoint}
        hasBatchSelect={hasBatchSelect}
      />,
    ];
  };

  const renderLeafRow = (row: GenericWidgetRow, depth: number) => {
    if (hoursOnly) {
      return renderHourRow(row, depth);
    }

    const rowId = +row.id!;
    const hours = row.hours || [];
    const isExpanded = widget._status.expandedRowIds.includes(rowId);
    const isLoadingSubRows = !agentOnly && widget._status.loadingRowIds.includes(rowId);
    const hasLoadedSubRows = widget._status.loadedRowIds.includes(rowId);
    const isEmpty = hasLoadedSubRows && !hours.length && !isSaving;
    const hasMoreSubRows = !hasLoadedSubRows && !isLoadingSubRows && !isEmpty;

    const manageableHours = hours.filter(hour => hour.canManage && hour.project?.active !== false);

    const selectedHours = manageableHours.filter(hour => !!hour.isSelected);
    const isSelected = selectedHours.length > 0 && selectedHours.length === manageableHours.length;
    const isIndeterminate =
      selectedHours.length > 0 && selectedHours.length < manageableHours.length;

    const canSelect = !agentOnly && !readOnly && isExpanded && !isEmpty && !!manageableHours.length;
    const agentFilters = row.agentActivity?.filters;

    return (
      <React.Fragment key={rowId}>
        <TableWidgetBodyRow
          canSelect={canSelect}
          data={row}
          depth={depth}
          columns={tableColumns}
          expandedState={!canExpand ? null : isExpanded}
          onToggleExpansion={() => handleLoadHours(rowId, !isExpanded, row.uri)}
          selectedState={isIndeterminate ? null : isSelected}
          onToggleSelection={
            onToggleSelection
              ? () =>
                  onToggleSelection?.({
                    selectionKey: 'hourIds',
                    value: manageableHours.map(hour => hour.id).filter(notNull),
                    batchRemove: selectedHours.length > 0,
                  })
              : undefined
          }
          isLoading={isLoadingSubRows}
          currency={currency}
          currentWidthBreakpoint={currentWidthBreakpoint}
        />
        {isExpanded &&
          agentColumns.length > 0 &&
          features.agent_tracking &&
          !config.isPublicReport &&
          agentFilters && (
            <AgentSessionRows
              key={JSON.stringify(agentFilters)}
              filters={agentFilters}
              columns={agentColumns.filter(column => column !== 'agent_sessions')}
              columnCount={tableColumns.length}
            />
          )}
        {!agentOnly && isExpanded && isEmpty && (
          <TableWidgetPlaceholderRow
            message={agentColumns.length ? 'No matching human entries.' : noHoursPlaceholder}
            depth={depth + 1}
            columnCount={columns.names.length}
          />
        )}
        {!agentOnly && isExpanded && agentColumns.length > 0 && hours.length > 0 && (
          <Tr>
            <Td colSpan={tableColumns.length * 2}>
              <strong>Human entries</strong>
            </Td>
          </Tr>
        )}
        {!agentOnly && !isLoadingSubRows && hours.map(hour => renderHourRow(hour, depth + 1))}
        {isLoadingSubRows && (
          <TableWidgetPlaceholderRow depth={depth + 1} columnCount={columns.names.length} />
        )}
        {!agentOnly && isExpanded && hasMoreSubRows && (
          <TableWidgetLoadMoreRow
            onLoadMore={() => handleLoadHours(rowId, true, row.uri)}
            columnCount={columns.names.length}
          />
        )}
      </React.Fragment>
    );
  };

  const renderSectionHeaderRow = (row: GenericWidgetRow, depth: number) => {
    const rowId = +row.id!;
    const isLoadingSubRows = widget._status.loadingRowIds.includes(rowId);

    return (
      <TableWidgetBodyRow
        key={rowId}
        columns={tableColumns}
        data={row}
        depth={depth}
        expandedState={null}
        isLoading={isLoadingSubRows}
        currentWidthBreakpoint={currentWidthBreakpoint}
        isSectionHeader
      />
    );
  };

  const renderProjectRows = (row: GenericWidgetRow, depth: number) => (
    <React.Fragment key={row.id}>
      {renderSectionHeaderRow(row, depth)}
      {row.projects?.map(project => renderLeafRow(project, depth))}
    </React.Fragment>
  );

  const canExpand = areWidgetRowsExpandable(widget.widget_type, hideEntries);

  /**
   * Recursively renders table rows and their children.
   */
  const renderRows = (
    rows: Array<GenericWidgetRow>,
    hasSubRows: React.MutableRefObject<boolean> = {
      current: false, // Not a real ref but same concept
    },
    depth = 0
  ): Array<React.ReactNode> => [
    ...rows.flatMap(row => {
      if (row.rows && row.rows.length) {
        hasSubRows.current = true;
        return [renderSectionHeaderRow(row, depth), ...renderRows(row.rows, hasSubRows, depth + 1)];
      }

      if (row.projects) {
        hasSubRows.current = true;
        return renderProjectRows(row, depth);
      }

      return renderLeafRow(row, depth);
    }),
  ];

  const headers = useMemo<Record<number, string>>(
    () =>
      tableColumns.reduce<Record<number, ReturnType<typeof columnNameFactory>>>(
        (headers, column, index) => {
          headers[index] = columnNameFactory(column, widget.widget_type);
          return headers;
        },
        {}
      ),
    [tableColumns, widget.widget_type]
  );

  if (isLoading) {
    return <TableWidgetLoading noHeader={noHeader} />;
  }

  const noRows = !widget.rows?.length;
  const noColumnsVisible = !tableColumns.length && (!hoursOnly || !hourColumns.length);

  if (noRows || noColumnsVisible) {
    const message = noColumnsVisible ? NO_DATA_COLUMNS_MESSAGE : NO_DATA_FILTERS_MESSAGE;
    return <NoData message={message} noHeader={noHeader} />;
  }

  if (widget.widget_type === WIDGET_LABELS_HBAR && tagFilterMode === TagFilterMode.AND) {
    return <NoData message={NO_DATA_TAG_FILTER_MESSAGE} />;
  }

  const leftAlignedColumns = [COLUMN_NAME, COLUMN_DAY, COLUMN_PROJECT, COLUMN_STATE];

  return (
    <Table
      inline
      rowHover
      rowLines={rowLines}
      ref={setTableRef}
      headers={headers}
      className={className}
    >
      <Thead>
        <Tr style={{ position: 'relative', zIndex: 1 }}>
          {tableColumns.map((name, index) => (
            <SortableTh
              key={name}
              field={name}
              isActive={name === columns.sort_by}
              direction={
                // For the name column we default sorting to ascending. For all other columns, we
                // default to descending
                (name === columns.sort_by ? columns.order === 'asc' : name === COLUMN_NAME)
                  ? 'asc'
                  : 'desc'
              }
              onClick={() => handleColumnChange(name as Column)}
              rightAligned={!leftAlignedColumns.includes(name)}
              colSpan={PROGRESS_BAR_COLUMNS.includes(name) ? 2 : undefined}
              className={stickyHeader ? styles.stickyHeader : undefined}
            >
              {index === 0 && batchSelectCheckbox}
              {columnNameFactory(name, widget.widget_type)}
            </SortableTh>
          ))}
        </Tr>
      </Thead>
      <Tbody>{renderRows(widget.rows!)}</Tbody>

      {widget.footer && renderFooterRow(widget.footer)}
    </Table>
  );
};

export default TableWidgetBody;
